// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

export enum COUNTRY_CODE {
    SG = 'SG',
    MY = 'MY'
}

export enum BUILDING_TYPE {
    LANDED_HOUSE = 'LANDED_HOUSE',
    CONDOMINIUM = 'CONDOMINIUM',
    HDB_FLAT = 'HDB_FLAT',
    RENTED_COMMERCIAL_PROPERTY = 'RENTED_COMMERCIAL_PROPERTY',
    OWNED_COMMERCIAL_PROPERTY = 'OWNED_COMMERCIAL_PROPERTY'
}

export enum BUILDING_ENERGY {
    AVERAGE = 'AVERAGE',
    INTENSIVE = 'INTENSIVE',
    LOW = 'LOW'
}

export enum ROOF_MATERIAL {
    METAL = 'METAL',
    TILES_SIMPLE = 'TILES_SIMPLE',
    TILES_COMPLEX = 'TILES_COMPLEX',
    TILES = 'TILES',
    CONCRETE = 'CONCRETE',
    MIXED = 'MIXED',
    OTHER = 'OTHER'
}

export enum RETAILER {
    SPGROUP = 'SPGROUP',
    GENECO = 'GENECO',
    KEPPEL_ELECTRIC = 'KEPPEL_ELECTRIC',
    SENOKO_ENERGY = 'SENOKO_ENERGY',
    UNION_POWER = 'UNION_POWER',
    TUAS_POWER_SUPPLY = 'TUAS_POWER_SUPPLY',
    SEMBCORP_POWER = 'SEMBCORP_POWER',
    DIAMOND_ELECTRIC = 'DIAMOND_ELECTRIC',
    PACIFIC_LIGHT_ENERGY = 'PACIFIC_LIGHT_ENERGY',
    SUNSEAP_ENERGY = 'SUNSEAP_ENERGY',
    OTHER = 'OTHER'
}

export enum METER_BOX {
    SINGLE_PHASE = 'SINGLE_PHASE',
    THREE_PHASE = 'THREE_PHASE',
    USER_UNSURE = 'USER_UNSURE'
}

export enum DAYTIME_CONSUMPTION {
    MORNING_AND_NIGHT = 'MORNING_AND_NIGHT',
    EQUALLY_THROUGHOUT_DAY = 'EQUALLY_THROUGHOUT_DAY',
    WEEKDAY_OFFICE = 'WEEKDAY_OFFICE',
    WEEKDAY_OFFICE_EXTENDED = 'WEEKDAY_OFFICE_EXTENDED',
    EVERYDAY_OFFICE = 'EVERYDAY_OFFICE',
    EVERYDAY_OFFICE_EXTENDED = 'EVERYDAY_OFFICE_EXTENDED',
    TWENTYFOUR_SEVEN = 'TWENTYFOUR_SEVEN',
    OTHER = 'OTHER'
}

export enum OFFER_TYPE {
    TEN_YEAR_RTO = 'TEN_YEAR_RTO',
    FIVE_YEAR_RTO = 'FIVE_YEAR_RTO',
    UPFRONT = 'UPFRONT',
    PPA = 'PPA'
}

export enum PRODUCT {
    RTO = 'RTO',
    QC = 'QC',
    PPA = 'PPA',
    DP = 'DP'
}

export enum TENDER_SELECTION {
    TEN_YEAR = 'TEN_YEAR',
    FIVE_YEAR = 'FIVE_YEAR'
}

// ---------------------------------------------------------------------------
// Input types
// ---------------------------------------------------------------------------

export interface AddressComponentInput {
    long_name: string;
    short_name: string;
    types: Array<string | null>;
}

export interface PolygonInput {
    lng: number;
    lat: number;
}

export interface UtmInput {
    source?: string | null;
    medium?: string | null;
    campaign?: string | null;
    term?: string | null;
    content?: string | null;
}

export interface BuildingInput {
    type: BUILDING_TYPE;
    energy?: BUILDING_ENERGY | null;
    lng: number;
    lat: number;
    height?: number | null;
    storeys?: number | null;
    utilities?: number | null;
    osm_id?: number | null;
    material?: ROOF_MATERIAL | null;
    retailer?: RETAILER | null;
    meter_box?: METER_BOX | null;
    daytime_consumption: DAYTIME_CONSUMPTION;
    area: number;
    available?: number | null;
    polygon: PolygonInput[];
}

export interface VendeeInput {
    name?: string | null;
    family_name?: string | null;
    email?: string | null;
    address: string;
    address_components: Array<AddressComponentInput | null>;
    company?: string | null;
    job_title?: string | null;
    phone?: string | null;
    timeframe?: string | null;
    referral?: string | null;
    building: BuildingInput;
    utm?: UtmInput | null;
}

export interface EmailInput {
    email: string;
}

export interface OfferInput {
    code: string;
    product: PRODUCT;
    term?: TENDER_SELECTION | null;
    waitlist?: boolean | null;
}

// ---------------------------------------------------------------------------
// Response / output types
// ---------------------------------------------------------------------------

export interface HighLow {
    high: string;
    low: string;
}

export interface QuoteInstallation {
    roi: string;
    cost: HighLow;
    generated: string;
    size: string;
    lease: string;
}

export interface QuoteSavings {
    utilities: HighLow;
    ppa: HighLow;
}

export interface QuoteImpact {
    cars: string;
    trees: string;
    co2: string;
}

export interface RtoOption {
    cost: number;
    payment: number;
}

export interface Quote {
    installation: QuoteInstallation;
    savings: QuoteSavings;
    impact: QuoteImpact;
    roof_size: number;
    rtoOptions?: RtoOption[] | null;
    applicableOfferTypes: OFFER_TYPE[];
    code: string;
}

export interface Polygon {
    lng: number;
    lat: number;
}

export interface Building {
    type: string;
    height?: number | null;
    storeys?: number | null;
    eui?: number | null;
    energy?: string | null;
    consumption?: number | null;
    osm_id?: number | null;
    roof?: string | null;
    material?: string | null;
    area: number;
    available?: number | null;
    irradiation?: number | null;
    utilities?: number | null;
    polygon: Polygon[];
}

export interface Assessment {
    id: string;
    building: Building;
}

export interface ListAssessments {
    total: number;
    current_page: number;
    max_page: number;
    assessments: Assessment[];
}

// ---------------------------------------------------------------------------
// Mutation / query variable & response types
// ---------------------------------------------------------------------------

export interface GetOfferVariables {
    country_code: COUNTRY_CODE;
    input: VendeeInput;
}

export interface GetOfferResponse {
    data: {
        getOffer: Quote;
    };
}

export interface SelectOfferVariables {
    country_code: COUNTRY_CODE;
    input: OfferInput;
}

export interface SelectOfferResponse {
    data: {
        selectOffer: string;
    };
}

export interface EmailVariables {
    input: EmailInput;
}

export interface EmailResponse {
    data: {
        email: string;
    };
}

export interface ListAssessmentsVariables {
    country_code: COUNTRY_CODE;
    page: number;
    lng: number;
    lat: number;
}

export interface ListAssessmentsResponse {
    data: {
        listAssessments: ListAssessments;
    };
}

export interface GetBuildingTypeVariables {
    country_code: COUNTRY_CODE;
    code: string;
}

export interface GetBuildingTypeResponse {
    data: {
        getBuildingType: BUILDING_TYPE;
    };
}

// ---------------------------------------------------------------------------
// getNearbyCustomerCount
// ---------------------------------------------------------------------------

export interface NearbyCustomerCount {
    count: number;
}

export interface GetNearbyCustomerCountVariables {
    countryCode: COUNTRY_CODE;
    lat: number;
    lng: number;
}

export interface GetNearbyCustomerCountResponse {
    data: {
        getNearbyCustomerCount: NearbyCustomerCount;
    };
}

// ---------------------------------------------------------------------------
// Generic GraphQL response wrapper (used by the base request function)
// ---------------------------------------------------------------------------

export interface GraphQLResponse<T = unknown> {
    data?: T;
    errors?: Array<{
        message: string;
        locations?: Array<{ line: number; column: number }>;
        path?: Array<string | number>;
    }>;
}
